import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import SearchBar from '@/components/feature/SearchBar';
import { usePageMeta } from '@/hooks/usePageMeta';
import { searchMovies } from '@/utils/media';
import type { MediaCategory } from '@/types/media';

type Tab = 'all' | MediaCategory;

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'all', label: '全部', icon: 'ri-apps-2-line' },
  { key: 'movie', label: '电影', icon: 'ri-film-line' },
  { key: 'tv', label: '电视剧', icon: 'ri-tv-2-line' },
];

export default function Search() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get('q') || '').trim();
  const [tab, setTab] = useState<Tab>('all');

  usePageMeta(
    keyword ? `“${keyword}”的搜索结果 - 嫩草影院` : '搜索影视 - 嫩草影院',
    keyword
      ? `嫩草影院为你找到与“${keyword}”相关的影视资源，支持电影与电视剧分类查看。`
      : '在嫩草影院搜索你想看的电影、电视剧。',
    keyword ? `${keyword},搜索影视,嫩草影院` : '搜索影视,嫩草影院',
  );

  useEffect(() => {
    setTab('all');
  }, [keyword]);

  const matched = useMemo(() => searchMovies(keyword), [keyword]);
  const counts = useMemo(
    () => ({
      all: matched.length,
      movie: matched.filter((m) => m.category === 'movie').length,
      tv: matched.filter((m) => m.category === 'tv').length,
    }),
    [matched],
  );
  const results = useMemo(
    () => (tab === 'all' ? matched : matched.filter((m) => m.category === tab)),
    [matched, tab],
  );

  return (
    <div className="flex min-h-screen flex-col bg-background-950">
      <Navbar />

      <main className="flex-1">
        <section className="border-b border-background-800 bg-background-900/50">
          <div className="mx-auto w-full max-w-4xl px-4 py-8 md:px-6 md:py-10">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-300">搜索结果</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-50 md:text-3xl">
              搜索影视
            </h1>
            <div className="mt-5 max-w-2xl">
              <SearchBar initialValue={keyword} size="lg" />
            </div>
            {keyword && (
              <p className="mt-4 text-sm text-foreground-400">
                关键词：<span className="font-bold text-primary-400">{keyword}</span>
                <span className="ml-3">
                  找到 <span className="font-bold text-foreground-100">{counts.all}</span> 个相关结果
                </span>
              </p>
            )}
          </div>
        </section>

        <div className="mx-auto w-full px-4 py-8 md:px-6">
          <div className="mb-6 inline-flex items-center gap-1 rounded-full bg-background-800 p-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-4 text-sm font-medium transition-colors ${
                  tab === t.key
                    ? 'bg-primary-500 text-background-950'
                    : 'text-foreground-300 hover:text-foreground-50'
                }`}
              >
                <i className={t.icon} />
                {t.label}
                <span className="text-xs opacity-80">({counts[t.key]})</span>
              </button>
            ))}
          </div>

          {!keyword ? (
            <EmptyState
              icon="ri-search-line"
              title="输入关键词开始搜索"
              desc="支持按影视名称模糊搜索，例如“星河”“长夜”“时间”。"
            />
          ) : results.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {results.map((movie) => (
                <MovieCard key={movie.id} movie={movie} showSummary />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-emotion-sad-line"
              title="未找到相关影视"
              desc="没有匹配到你想找的内容，建议调整关键词后重新搜索。"
              action={
                <Link
                  to="/"
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
                >
                  <i className="ri-home-4-line" />
                  返回首页
                </Link>
              }
            />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

interface EmptyStateProps {
  icon: string;
  title: string;
  desc: string;
  action?: ReactNode;
}

function EmptyState({ icon, title, desc, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-background-800 bg-background-900 px-6 py-20 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-500">
        <i className={`${icon} text-3xl`} />
      </span>
      <h3 className="mt-5 font-heading text-lg font-bold text-foreground-100">{title}</h3>
      <p className="mt-2 max-w-md text-sm text-foreground-400">{desc}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}